const howToInstallCpanelInVpsPost = {
  slug: "how-to-install-cpanel-in-vps",
  image: "/blog-images/cpanel-plesk-hosting-control.jpg",
  title: "How to Install cPanel in VPS (Step-by-Step 2026 Guide) 🛠️",
  metaTitle: "How to Install cPanel on VPS - Complete 2026 Setup Guide",
  metaDescription:
    "Learn how to install cPanel on a VPS with this step-by-step 2026 guide. Includes prerequisites, commands, licensing notes, firewall setup, and troubleshooting.",
  keywords: [
    "install cpanel on vps",
    "how to install cpanel",
    "cpanel vps setup",
    "whm installation guide",
    "linux vps control panel",
    "cpanel firewall ports",
    "clubhosty cpanel hosting",
    "vps server management",
  ],
  excerpt:
    "A practical guide to install cPanel on a VPS safely, including server requirements, installation commands, and post-install hardening.",
  category: "VPS",
  date: "April 5, 2026",
  readTime: "10 min read",
  language: "en-IN",
  clubhostyLinks: [
    {
      label: "ClubHosty cPanel Hosting",
      url: "https://www.clubhosty.com/affordable-cpanel-hosting",
    },
    {
      label: "ClubHosty VPS Hosting",
      url: "https://www.clubhosty.com/affordable-vps-hosting",
    },
    {
      label: "ClubHosty Contact Support",
      url: "https://www.clubhosty.com/contact",
    },
  ],
  faqs: [
    {
      question: "Can I install cPanel on any Linux distribution?",
      answer:
        "No, cPanel officially supports specific distributions such as AlmaLinux, Rocky Linux, and CloudLinux.",
    },
    {
      question: "How long does cPanel installation take on VPS?",
      answer:
        "It usually takes between 20 and 60 minutes depending on server resources and network speed.",
    },
    {
      question: "Do I need a license before using cPanel?",
      answer:
        "Yes, cPanel is commercial software and requires a valid license for ongoing use.",
    },
  ],
  relatedPosts: [
    {
      slug: "change-ssh-port",
      title: "How to Change SSH Port Safely - Complete Security Guide",
    },
    {
      slug: "shared-vs-vps-vs-cloud-hosting-2026",
      title: "Shared vs VPS vs Cloud Hosting - Which is Best?",
    },
    {
      slug: "what-is-openclaw-self-hosted-ai-agent-guide",
      title: "What Is OpenClaw? Self-Hosted AI Agent Guide",
    },
  ],
  content: [
    {
      heading: "How to Install cPanel in VPS (2026 Guide) 🚀",
      headingLevel: "h1",
      paragraphs: [
        "Installing cPanel on a VPS makes server management easier with a visual interface for websites, email, DNS, and databases.",
        "This guide walks you through a safe installation flow with prerequisites, commands, and checks so you do not break your server setup.",
      ],
    },
    {
      heading: "Before You Start 📋",
      headingLevel: "h2",
      paragraphs: [
        "cPanel is a commercial control panel and needs a valid license. Confirm your VPS meets the minimum requirements before starting.",
      ],
      list: [
        "A fresh VPS with root SSH access",
        "Supported OS: AlmaLinux, Rocky Linux, or CloudLinux (latest stable)",
        "Minimum 2 GB RAM (4 GB+ recommended)",
        "At least 20 GB free disk space",
        "A valid hostname (FQDN), for example: server1.example.com",
        "cPanel license (trial or paid)",
      ],
    },
    {
      heading: "Important Notes ⚠️",
      headingLevel: "h2",
      list: [
        "Do not install cPanel on a server already running another control panel",
        "Use a clean OS image to avoid package conflicts",
        "Set hostname before installation",
        "Keep SSH open during the full install process",
      ],
    },
    {
      heading: "Step 1: Connect to Your VPS via SSH 🔐",
      headingLevel: "h2",
      paragraphs: [
        "Log in as root from your terminal.",
      ],
      code: "ssh root@your_server_ip",
    },
    {
      heading: "Step 2: Set a Valid Hostname 🏷️",
      headingLevel: "h2",
      paragraphs: [
        "cPanel requires a valid fully qualified domain name.",
      ],
      code: "hostnamectl set-hostname server1.example.com\n\nhostnamectl status",
    },
    {
      heading: "Step 3: Update System Packages ⬆️",
      headingLevel: "h2",
      paragraphs: [
        "Update all packages before running the installer.",
      ],
      code: "dnf update -y",
    },
    {
      heading: "Step 4: Disable NetworkManager (If Required) 🌐",
      headingLevel: "h2",
      paragraphs: [
        "On some setups, cPanel works better with standard network scripts. Verify cPanel documentation for your exact OS version before disabling.",
      ],
      code: "systemctl stop NetworkManager\nsystemctl disable NetworkManager",
    },
    {
      heading: "Step 5: Install Perl (Required by Installer) 🧩",
      headingLevel: "h2",
      code: "dnf install perl -y",
    },
    {
      heading: "Step 6: Download and Run cPanel Installer 📥",
      headingLevel: "h2",
      paragraphs: [
        "This step can take 20 to 60 minutes depending on VPS performance and network speed.",
      ],
      code: "cd /home\ncurl -o latest -L https://securedownloads.cpanel.net/latest\nsh latest",
    },
    {
      heading: "Step 7: Access cPanel/WHM After Install 🌍",
      headingLevel: "h2",
      paragraphs: [
        "After the installer completes, open WHM in your browser and finish the setup wizard.",
      ],
      code: "https://your_server_ip:2087",
    },
    {
      heading: "Step 8: Open Required Firewall Ports 🔥",
      headingLevel: "h2",
      paragraphs: [
        "Allow essential cPanel, WHM, and web traffic ports.",
      ],
      code: "# SSH\nfirewall-cmd --permanent --add-port=22/tcp\n# HTTP/HTTPS\nfirewall-cmd --permanent --add-port=80/tcp\nfirewall-cmd --permanent --add-port=443/tcp\n# WHM/cPanel/Webmail\nfirewall-cmd --permanent --add-port=2087/tcp\nfirewall-cmd --permanent --add-port=2083/tcp\nfirewall-cmd --permanent --add-port=2096/tcp\n\nfirewall-cmd --reload",
    },
    {
      heading: "Recommended Post-Install Security ✅",
      headingLevel: "h2",
      list: [
        "Change SSH port and disable root password login",
        "Enable key-based SSH authentication",
        "Install and configure CSF/LFD or Fail2ban",
        "Enable automatic backups",
        "Force HTTPS in WHM and cPanel",
        "Keep system and cPanel packages updated",
      ],
    },
    {
      heading: "Common Installation Errors and Fixes 🛠️",
      headingLevel: "h2",
      list: [
        "Hostname invalid: set a proper FQDN and retry",
        "Not enough RAM/disk: upgrade VPS resources",
        "License issue: activate or verify cPanel license",
        "Installer failed midway: review logs and rerun installer",
      ],
      code: "# Useful logs\ncat /var/log/cpanel-install.log\ncat /usr/local/cpanel/logs/error_log",
    },
    {
      heading: "Final Thoughts 💡",
      headingLevel: "h2",
      paragraphs: [
        "A properly installed cPanel VPS saves time and simplifies server operations, especially for hosting multiple websites.",
        "If you plan to run cPanel long-term, choose a reliable VPS provider with stable performance and quick support, then scale resources as traffic grows.",
      ],
    },
  ],
};

export default howToInstallCpanelInVpsPost;
